/**
 * A state diagram paired with one machine (decision 030): made FROM an existing
 * machine, or WITH a new one - Patrick asked for both.
 *
 * The view exposes exactly `M::**`, which is what pairs it: a state put on the
 * diagram then becomes a usage in M. One request and one commit, so a refused
 * view leaves no new machine behind.
 *
 * The name is defaulted to "<machine> diagram". A view named like the machine
 * beside it in one package is refused - two elements of one name in one place -
 * so the obvious default would be the one that fails.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { ApiError, api } from '../../api'
import type { EntityRow, PackageRow } from '../../schema'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import ElementPicker from '../common/ElementPicker'
import { elementOption } from '../common/elements'
import { isMachine, packageOf } from './stateViews'

export type StateViewMode = 'existing' | 'new'

export default function CreateStateViewDialog({
  mode,
  onClose,
  onCreated,
}: {
  mode: StateViewMode
  onClose: () => void
  onCreated: (identifier: string) => void
}) {
  const toasts = useToasts()
  const [packages, setPackages] = useState<PackageRow[]>([])
  const [machines, setMachines] = useState<EntityRow[]>([])
  const [pkg, setPkg] = useState('')
  const [machine, setMachine] = useState('')
  const [machineId, setMachineId] = useState('')
  const [machineName, setMachineName] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [named, setNamed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  // Behavior and under (issue #114): the view and any new machine go there.
  const [packageWarning, setPackageWarning] = useState('')
  useEffect(() => {
    api
      .packagesFor('view')
      .then((answer) => {
        setPackages(answer.items)
        setPackageWarning(answer.warning)
      })
      .catch(() => setPackages([]))
    if (mode === 'existing') {
      api
        .list('states', { limit: 5000 })
        .then((page) => setMachines(page.items.filter(isMachine)))
        .catch(() => setMachines([]))
    }
  }, [mode])

  const chosen = machines.find((row) => row.id === machine)
  const machineLabel = mode === 'existing' ? String(chosen?.name ?? '') : machineName || machineId

  // The name follows the machine until somebody types one of their own.
  const shownName = named ? name : machineLabel ? `${machineLabel} diagram` : ''

  const pickMachine = (value: string) => {
    setMachine(value)
    const row = machines.find((entry) => entry.id === value)
    if (row && !pkg) setPkg(packageOf(String(row.qualified_name ?? '')))
  }

  const ready = Boolean(pkg && identifier) && (mode === 'existing' ? Boolean(machine) : Boolean(machineId || machineName))

  const submit = () => {
    setBusy(true)
    setFailure('')
    api
      .createStateView({
        package: pkg,
        identifier,
        ...(shownName ? { name: shownName } : {}),
        ...(mode === 'existing'
          ? { machine }
          : { new_machine: { identifier: machineId, ...(machineName ? { name: machineName } : {}) } }),
      })
      .then((created) => {
        toasts.succeeded(`${created.identifier} created, showing ${created.machine.split('::').pop()}`)
        onCreated(created.identifier)
      })
      .catch((error: ApiError) => setFailure(error.message))
      .finally(() => setBusy(false))
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'existing' ? 'New state diagram of a machine' : 'New state diagram with a new machine'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          {mode === 'existing' ? (
            <ElementPicker
              rows={machines}
              label="Machine"
              required
              value={machine || null}
              onChange={pickMachine}
              describe={(row, value) => ({ ...elementOption(row, value), chosen: `${row.name || row.id} (${row.id})` })}
              helperText="The diagram shows this machine and the states in it"
            />
          ) : (
            <>
              <TextField
                label="Machine ID"
                required
                size="small"
                value={machineId}
                onChange={(event) => setMachineId(event.target.value)}
                helperText="The new state def's short name, e.g. MD_POWER"
                slotProps={{ htmlInput: { style: { fontFamily: MONOSPACE } } }}
              />
              <TextField
                label="Machine name"
                size="small"
                value={machineName}
                onChange={(event) => setMachineName(event.target.value)}
                helperText="What transitions and diagrams call it; defaults to the ID"
              />
            </>
          )}

          <ElementPicker
            options={packages.map((entry) => ({ value: entry.qualified_name }))}
            rows={[]}
            label="Package"
            required
            value={pkg || null}
            onChange={setPkg}
            helperText={packageWarning || (mode === 'existing' ? 'Where the view is declared; the machine’s own by default' : 'Where the view and the new machine are declared')}
          />

          <TextField
            label="View ID"
            required
            size="small"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            helperText="The view's short name, e.g. VW_POWER"
            slotProps={{ htmlInput: { style: { fontFamily: MONOSPACE } } }}
          />
          <TextField
            label="View name"
            size="small"
            value={shownName}
            onChange={(event) => {
              setNamed(true)
              setName(event.target.value)
            }}
            helperText="Not the machine's own name: two elements of one name cannot share a package"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!ready || busy}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
